import type { ComponentProps, ReactNode } from 'react';

/** v2 button primitive. Primary = brand fill (ink text); ghost = bordered surface. */
type Variant = 'primary' | 'ghost' | 'danger';

/**
 * The tech shell's thumb target — 56px tall, full width, and bigger type than the office.
 *
 * ⭐ Not a `Variant`: it is a SIZE and a shape, applied through `className` to both
 * `Button` and the anchors beside them (Directions and Call are links, not buttons, and
 * they have to match the row they sit in). A variant could not reach an `<a>`.
 *
 * ⭐ It was private to `TechVisitPage` until the next-stop card became a second reader
 * (117). Two files hard-coding `min-h-[56px]` is how one of them quietly stops matching
 * the other — and these two render the same three actions one screen apart.
 */
export const bigButton =
  'flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[var(--radius)] text-base font-bold';

/**
 * A square icon-only button in a settings list row (move, delete): a 44px hit target around
 * a 14–16px icon. Give it an `aria-label`; the icon says nothing to a screen reader.
 *
 * Dimmed whether it is `disabled` or `aria-disabled`. The second keeps focus on a control
 * that locks while its own write saves (see `ServiceTypesSettingsCard`).
 */
export const iconButton =
  'flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-[var(--radius-sm)] text-muted-foreground hover:bg-surface-2 hover:text-text disabled:pointer-events-none disabled:opacity-40 aria-disabled:pointer-events-none aria-disabled:opacity-40';

/** Every button's shape: size, type and the hover fade. */
const shape =
  'inline-flex items-center justify-center gap-2 rounded-[var(--radius)] px-4 py-2 text-sm font-semibold transition-colors';

/**
 * `sm`: a row's secondary action inside a dense list (the crew map panel's "Open job" and
 * "<Name>'s day"), a size and nothing else: the same variants, the same global focus ring.
 */
const smallShape =
  'inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-sm)] px-2 py-1 text-xs font-medium transition-colors';

const styles: Record<Variant, string> = {
  primary: 'bg-brand text-ink hover:bg-[#ff7c4b]',
  ghost: 'border border-border bg-surface text-text hover:bg-surface-2',
  danger: 'bg-danger text-white hover:opacity-90',
};

/**
 * The ghost button's look, for a LINK that sits among buttons (Open the register, an
 * invoice, a PDF, Settings). It navigates, so it must be an anchor: middle-click, ⌘-click and
 * "open in new tab" work on one and on no button. `Button` has no `asChild`, and this is the
 * one class list those links share instead of four hand-copied ones drifting apart. Built
 * from the same pieces as `<Button variant="ghost">`.
 */
export const ghostButton = `${shape} ${styles.ghost}`;

/**
 * `ref` reaches the `<button>` (a plain prop in React 19), for a page that hands focus back
 * to the button that opened a step once the step closes.
 */
export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ComponentProps<'button'> & { variant?: Variant; size?: 'md' | 'sm' }) {
  return (
    <button
      type="button"
      className={`${size === 'sm' ? smallShape : shape} disabled:pointer-events-none disabled:opacity-60 aria-disabled:pointer-events-none aria-disabled:opacity-60 ${styles[variant]} ${className}`}
      {...props}
    />
  );
}

/**
 * A figure that opens what is behind it in a side panel (`KpiCard`'s `onOpen`, the Revenue
 * page's people): a full-width, left-aligned button that says it opens a dialog
 * (`aria-haspopup`), and lights on hover, reaching a little past its text so the light does not
 * touch the letters. Named by what it holds; the focus ring is the global one. Its children must
 * be phrasing content (spans): a button holds nothing else.
 */
export function FigureButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-haspopup="dialog"
      onClick={onClick}
      className="-mx-2 -my-1 block w-[calc(100%+1rem)] rounded-[var(--radius-sm)] px-2 py-1 text-left hover:bg-surface-2"
    >
      {children}
    </button>
  );
}
